import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { sameResourceId } from '../api/tags'
import { resourceScopeCleared, userScopeCleared } from '../session/scope'
import { definePersistenceEntry } from '../persistence/registry'
import type { ModulePersistenceEntry, SliceDeclaration } from '../persistence/types'
import type { WindowSnapshot } from './types'

/**
 * Вкладки и «откуда пришли» — перенос `frontend/src/modules/windows` (карточка 274).
 *
 * **Вкладку создаёт человек кнопкой «Вкладка», а не переход.** Навигация по
 * меню, ссылкам и кнопкам вкладок не заводит. Вкладка — окно (identity: имя +
 * параметры, включая rid), а не адрес. Возврат («откуда пришли») ядро помнит
 * для каждого окна само, независимо от полосы вкладок.
 *
 * Снимок вкладки хранит выбор, а не данные сервера (CLAUDE.md §7).
 */
export interface WindowTab {
  /** Identity окна: `onec.product:<rid>/<key>`. Одна вкладка на окно. */
  id: string
  kind: string
  /** Ресурс окна, если оно ресурсное: по нему вкладка уходит при сбросе ресурса. */
  resourceId: string | null
  /** Где человек был в окне, когда уходил: путь с параметрами, без `?w=`. */
  location: string
  title: string
  snapshot: WindowSnapshot | null
  at: string
}

export interface WindowReturn {
  /** Адрес, где человек был до окна, вместе с окнами поверх экрана. */
  from: string
  resourceId: string | null
}

export interface WindowsState {
  /** Вкладки в порядке создания: новая — в конце. */
  tabs: WindowTab[]
  /** identity окна → откуда в него пришли. */
  returns: Record<string, WindowReturn>
}

/** Сверх предела вкладка не создаётся и старую не вытесняет: её положил человек. */
export const MAX_TABS = 10
/** Предел «откуда пришли»: чтобы sessionStorage не рос за долгую смену. */
export const MAX_RETURNS = 50

/**
 * Сроки жизни частей слайса окон (D-070). Composition добавляет префикс модуля
 * оболочки (`shell.windowTabs`).
 */
export const WINDOWS_SLICES: readonly SliceDeclaration[] = [
  { name: 'windowTabs', lifetime: 'device', reason: 'вкладки кладёт человек; они обязаны пережить перезагрузку' },
  { name: 'windowReturns', lifetime: 'session', reason: 'возврат переживает F5, но не следующий день' },
]

const initialState: WindowsState = { tabs: [], returns: {} }

function isText(value: unknown): value is string {
  return typeof value === 'string'
}

/** Проверка снимка вкладок из хранилища (D-072): чужое и битое отбрасывается. */
export function decodeWindowTabs(raw: unknown): WindowTab[] | undefined {
  if (!Array.isArray(raw)) return undefined
  return (
    raw
      .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
      .filter((item) => isText(item.id) && isText(item.kind) && isText(item.location) && item.location.startsWith('/'))
      // Одна вкладка на окно: дубль из повреждённой записи отбрасывается.
      .filter((item, index, all) => all.findIndex((other) => other.id === item.id) === index)
      .slice(0, MAX_TABS)
      .map((item) => ({
        id: item.id as string,
        kind: item.kind as string,
        resourceId: isText(item.resourceId) ? item.resourceId : null,
        location: item.location as string,
        title: isText(item.title) ? item.title : '',
        snapshot:
          typeof item.snapshot === 'object' && item.snapshot !== null && !Array.isArray(item.snapshot)
            ? (item.snapshot as WindowSnapshot)
            : null,
        at: isText(item.at) ? item.at : new Date(0).toISOString(),
      }))
  )
}

export function decodeWindowReturns(raw: unknown): Record<string, WindowReturn> | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined
  const result: Record<string, WindowReturn> = {}
  for (const [window, value] of Object.entries(raw)) {
    if (typeof value !== 'object' || value === null) continue
    const item = value as Record<string, unknown>
    if (!isText(item.from) || !item.from.startsWith('/')) continue
    result[window] = { from: item.from, resourceId: isText(item.resourceId) ? item.resourceId : null }
  }
  return result
}

const windowsSlice = createSlice({
  name: 'windows',
  initialState,
  reducers: {
    tabsRestored(state, action: PayloadAction<WindowTab[]>) {
      state.tabs = action.payload.slice(0, MAX_TABS)
    },
    returnsRestored(state, action: PayloadAction<Record<string, WindowReturn>>) {
      state.returns = action.payload
    },
    /** Человек нажал «Вкладка»: окно встаёт в конец полосы. */
    tabCreated(state, action: PayloadAction<WindowTab>) {
      if (state.tabs.length >= MAX_TABS) return
      if (state.tabs.some((tab) => tab.id === action.payload.id)) return
      state.tabs.push(action.payload)
    },
    /** Человек уходит из окна вкладки — запомнить, где и чем он в нём был занят. */
    tabSaved(state, action: PayloadAction<Pick<WindowTab, 'id' | 'location' | 'title' | 'snapshot' | 'at'>>) {
      const tab = state.tabs.find((item) => item.id === action.payload.id)
      if (tab === undefined) return
      tab.location = action.payload.location
      tab.title = action.payload.title
      tab.snapshot = action.payload.snapshot
      tab.at = action.payload.at
    },
    /** Убрать вкладку из полосы. С экрана человек при этом никуда не уходит. */
    tabClosed(state, action: PayloadAction<string>) {
      state.tabs = state.tabs.filter((tab) => tab.id !== action.payload)
    },
    returnRecorded(state, action: PayloadAction<{ window: string } & WindowReturn>) {
      const { window, ...entry } = action.payload
      delete state.returns[window]
      state.returns[window] = entry
      const windows = Object.keys(state.returns)
      for (const stale of windows.slice(0, Math.max(0, windows.length - MAX_RETURNS))) delete state.returns[stale]
    },
    returnForgotten(state, action: PayloadAction<string>) {
      delete state.returns[action.payload]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userScopeCleared, () => initialState)
      .addCase(resourceScopeCleared, (state, action) => {
        const { resourceId } = action.payload
        state.tabs = state.tabs.filter((tab) => !sameResourceId(tab.resourceId, resourceId))
        for (const [window, entry] of Object.entries(state.returns)) {
          if (sameResourceId(entry.resourceId, resourceId)) delete state.returns[window]
        }
      })
  },
})

export const { tabsRestored, returnsRestored, tabCreated, tabSaved, tabClosed, returnRecorded, returnForgotten } =
  windowsSlice.actions

export const windowsReducer = windowsSlice.reducer

export const selectWindowTabs = (state: WindowsState): readonly WindowTab[] => state.tabs
export const selectWindowReturns = (state: WindowsState): Readonly<Record<string, WindowReturn>> => state.returns

/** Путь без параметров. */
export function pathOfLocation(location: string): string {
  const query = location.indexOf('?')
  return query < 0 ? location : location.slice(0, query)
}

/**
 * Записи реестра хранения для окон. Обе — пользовательские: вкладки одного
 * человека не достаются следующему на том же устройстве (F07).
 */
export const WINDOWS_PERSISTENCE: readonly ModulePersistenceEntry<WindowsState>[] = [
  definePersistenceEntry({
    key: 'shell.window-tabs',
    slice: 'windowTabs',
    lifetime: 'device',
    scope: 'user',
    version: 1,
    pick: (state) => state.tabs,
    decode: decodeWindowTabs,
    restore: (value) => tabsRestored(value),
  }),
  definePersistenceEntry({
    key: 'shell.window-returns',
    slice: 'windowReturns',
    lifetime: 'session',
    scope: 'user',
    version: 1,
    pick: (state) => state.returns,
    decode: decodeWindowReturns,
    restore: (value) => returnsRestored(value),
  }),
]
